(() => {
  const section_selector = '[data-routine_steps_section]';
  const featured_selector = '[data-routine_featured]';
  const swap_button_selector = '[data-routine_swap_button]';
  const add_all_selector = '[data-routine_add_all]';
  const status_selector = '[data-routine_status]';
  const product_template_selector = 'template[data-routine_product_template]';
  const step_selector = '.routine-steps__step[data-step-index]';

  const get_variant_id = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  };

  const get_sections_to_bundle = () => {
    const sections_to_bundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections_to_bundle } })
    );
    return sections_to_bundle;
  };

  const set_status = (section, message, state = 'info') => {
    const status = section.querySelector(status_selector);
    if (!status) return;
    status.dataset.state = state;
    status.textContent = message || '';
  };

  const set_loading = (button, is_loading) => {
    if (!button) return;
    const method = is_loading ? 'add' : 'remove';
    button.classList[method]('m-spinner-loading');
    button.disabled = !!is_loading;
    button.setAttribute('aria-disabled', is_loading ? 'true' : 'false');
  };

  const get_featured_node = (section, step_index) =>
    section.querySelector(`${featured_selector}[data-step-index="${step_index}"]`);

  const get_product_template = (section, step_index, variant_id) =>
    section.querySelector(
      `${product_template_selector}[data-step-index="${step_index}"][data-variant-id="${variant_id}"]`
    );

  const update_selected_thumb = (section, step_index, variant_id) => {
    const step_root = section.querySelector(`.routine-steps__step[data-step-index="${step_index}"]`);
    if (!step_root) return;

    const thumb_items = step_root.querySelectorAll('.routine-steps__thumb-item');
    thumb_items.forEach((thumb_item) => {
      const button = thumb_item.querySelector(swap_button_selector);
      const thumb_variant_id = get_variant_id(button?.getAttribute('data-variant-id'));
      thumb_item.classList.toggle('is-selected', thumb_variant_id === variant_id);
    });
  };

  const select_product_for_step = (section, step_index, variant_id) => {
    const featured = get_featured_node(section, step_index);
    if (!featured) return false;

    const template = get_product_template(section, step_index, variant_id);
    if (!template) return false;

    featured.innerHTML = template.innerHTML;
    featured.setAttribute('data-variant-id', String(variant_id));

    update_selected_thumb(section, step_index, variant_id);
    return true;
  };

  const set_active_step = (section, step_index) => {
    if (!section) return;
    const steps = section.querySelectorAll(step_selector);
    if (!steps?.length) return;

    steps.forEach((step) => {
      const is_active = step.getAttribute('data-step-index') === String(step_index);
      step.classList.toggle('is-active', is_active);
    });
  };

  const init_active_step = (section) => {
    const first_step = section.querySelector(step_selector);
    const first_step_index = first_step?.getAttribute('data-step-index');
    if (first_step_index == null) return;
    set_active_step(section, first_step_index);
  };

  const get_all_selected_items = (section) => {
    const featured_nodes = [...section.querySelectorAll(featured_selector)];
    const selected_items = featured_nodes.map((node) => get_variant_id(node.getAttribute('data-variant-id')));
    return { featured_nodes, selected_items };
  };

  const get_cart_add_url = () => {
    if (window.MinimogSettings?.routes?.cart_add_url) return window.MinimogSettings.routes.cart_add_url;
    return '/cart/add.js';
  };

  const build_fetch_config = () => {
    if (typeof fetchConfig === 'function') return fetchConfig('javascript');
    return {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  };

  const open_cart_ui = (response, section) => {
    const minimog_settings = window.MinimogSettings;
    const minimog_strings = window.MinimogStrings;
    const minimog_theme = window.MinimogTheme;
    const minimog_events = window.MinimogEvents;

    if (!minimog_settings) return;

    if (minimog_settings.use_ajax_atc) {
      const cart_drawer = document.querySelector('m-cart-drawer');
      if (cart_drawer && minimog_settings.enable_cart_drawer && minimog_theme?.Notification) {
        minimog_theme.Notification.show({
          target: cart_drawer.querySelector('m-cart-items'),
          method: 'prepend',
          type: 'success',
          message: minimog_strings?.itemAdded || 'Added to cart',
          delay: 400,
        });
        cart_drawer.open();
      } else if (minimog_theme?.Notification) {
        minimog_theme.Notification.show({
          target: section.querySelector(status_selector) || document.body,
          method: 'appendChild',
          type: 'success',
          message: minimog_strings?.itemAdded || 'Added to cart',
          last: 3000,
          sticky: true,
        });
      }

      if (minimog_events?.emit && minimog_theme?.pubSubEvents?.cartUpdate) {
        minimog_events.emit(minimog_theme.pubSubEvents.cartUpdate, { cart: response });
      }

      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: { product: response },
        })
      );
    } else if (minimog_settings?.routes?.cart) {
      window.location = minimog_settings.routes.cart;
    }
  };

  const add_entire_routine_to_cart = async (section, button) => {
    const { featured_nodes, selected_items } = get_all_selected_items(section);

    const missing_count = selected_items.filter((id) => !id).length;
    if (missing_count > 0) {
      set_status(section, 'Please select an available product for each step.', 'error');
      return;
    }

    const items = selected_items.map((id) => ({ id, quantity: 1 }));
    if (items.length === 0 || featured_nodes.length === 0) return;

    set_status(section, '', 'info');
    set_loading(button, true);

    const sections_to_bundle = get_sections_to_bundle();
    const data = {
      items,
      sections: sections_to_bundle,
      sections_url: window.location.pathname,
    };

    try {
      const config = build_fetch_config();
      config.method = 'POST';
      config.body = JSON.stringify(data);

      const response = await fetch(get_cart_add_url(), config);
      const json = await response.json();

      if (json?.status) {
        set_status(section, json.description || json.message || 'Failed to add routine to cart.', 'error');
        document.dispatchEvent(
          new CustomEvent('product-ajax:error', {
            detail: { errorMessage: json.description || json.message },
          })
        );
        return;
      }

      set_status(section, '', 'success');
      open_cart_ui(json, section);
    } catch (error) {
      set_status(section, 'An error occurred while adding the routine to cart.', 'error');
      // eslint-disable-next-line no-console
      console.error('[routine-steps] add to cart failed', error);
    } finally {
      set_loading(button, false);
    }
  };

  const on_section_click = (evt, section) => {
    const step_root = evt.target.closest(step_selector);
    const step_index_for_click = step_root?.getAttribute('data-step-index');
    if (step_index_for_click != null) set_active_step(section, step_index_for_click);

    const swap_button = evt.target.closest(swap_button_selector);
    if (swap_button && section.contains(swap_button)) {
      evt.preventDefault();
      if (swap_button.disabled) return;

      const step_index = swap_button.getAttribute('data-step-index');
      const variant_id = get_variant_id(swap_button.getAttribute('data-variant-id'));
      if (!variant_id) return;

      set_active_step(section, step_index);
      select_product_for_step(section, step_index, variant_id);
      return;
    }

    const add_all_button = evt.target.closest(add_all_selector);
    if (add_all_button && section.contains(add_all_button)) {
      evt.preventDefault();
      add_entire_routine_to_cart(section, add_all_button);
    }
  };

  const init_section = (section) => {
    if (!section || section.getAttribute('data-routine_steps_initialized') === 'true') return;
    section.setAttribute('data-routine_steps_initialized', 'true');

    section.addEventListener('click', (evt) => on_section_click(evt, section));
    init_active_step(section);
  };

  const init_all = () => {
    document.querySelectorAll(section_selector).forEach(init_section);
  };

  document.addEventListener('DOMContentLoaded', init_all);

  document.addEventListener('shopify:section:load', (evt) => {
    const loaded_section = evt.target;
    if (!loaded_section) return;

    if (loaded_section.matches?.(section_selector)) {
      init_section(loaded_section);
      return;
    }

    loaded_section.querySelectorAll?.(section_selector).forEach(init_section);
  });
})();


